import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FlaskConical, Loader2, Plus, Trash2 } from "lucide-react";
import { api, unwrapArray, unwrapObject } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { SearchSelect } from "@/components/ui/search-select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { dateLocale } from "@/lib/dateTime";
import { SampleStatusBadge } from "./SampleStatusBadge";
import type { SampleRecord } from "./sampleTypes";

interface Row { key: number; product_id: string; label: string; custom: string; quantity: string; unit: string }

const blank = (key: number): Row => ({ key, product_id: "", label: "", custom: "", quantity: "1", unit: "NOS" });

/** The samples of a deal or lead, and the button that asks for one */
export function SamplesCard({ dealId, leadId, canRequest = true }: { dealId?: string; leadId?: string; canRequest?: boolean }) {
  const navigate = useNavigate();
  const { success, error } = useToast();
  const { hasPermission } = useAuth();
  const [rows, setRows] = useState<SampleRecord[] | null>(null);
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<Row[]>([blank(1)]);
  const [shipTo, setShipTo] = useState("");
  const [mode, setMode] = useState<"SHIPPED" | "DIGITAL">("SHIPPED");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const canRead = hasPermission("sample.read");
  const canCreate = hasPermission("sample.create") && canRequest;

  const load = async () => {
    try {
      setRows(unwrapArray<SampleRecord>(await api.get(dealId ? `/api/Sample/ByDeal/${dealId}` : `/api/Sample/ByLead/${leadId}`)));
    } catch {
      setRows([]);
    }
  };
  useEffect(() => {
    if (canRead) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealId, leadId, canRead]);

  if (!canRead || rows === null) return null;
  if (rows.length === 0 && !canCreate) return null;

  const setRow = (key: number, patch: Partial<Row>) => setItems((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch } : r)));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const list = items.filter((r) => r.product_id || r.custom.trim());
    if (!list.length) return error("Add an item", "Choose a product or name what is being sent");
    setBusy(true);
    try {
      const res: any = await api.post("/api/Sample/Create", {
        deal_id: dealId,
        lead_id: leadId,
        items: list.map((r) => ({ product_id: r.product_id || undefined, product_name: r.product_id ? undefined : r.custom.trim(), quantity: Number(r.quantity) || 1, unit: r.unit || undefined })),
        delivery_mode: mode,
        ship_to: mode === "SHIPPED" ? shipTo.trim() || undefined : undefined,
        notes: notes.trim() || undefined,
      });
      const created = unwrapObject<SampleRecord>(res);
      success("Sample requested", created.request_number);
      setOpen(false);
      navigate(`/samples/${created.id}`);
    } catch (err: any) {
      error("Could not request the sample", err?.message || "Try again");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-card border rounded-2xl shadow-xs overflow-hidden">
      <div className="flex items-center justify-between gap-3 px-4 py-3 border-b">
        <div className="flex items-center gap-2">
          <FlaskConical className="size-4 text-primary" />
          <h3 className="text-sm font-bold text-foreground">Samples</h3>
          {rows.length > 0 && <span className="text-[10px] px-1.5 rounded-full bg-muted text-muted-foreground">{rows.length}</span>}
        </div>
        {canCreate && (
          <Button size="sm" variant="outline" className="h-8 gap-1.5 text-xs" onClick={() => (setItems([blank(1)]), setShipTo(""), setNotes(""), setMode("SHIPPED"), setOpen(true))}>
            <Plus className="size-3.5" /> Request sample
          </Button>
        )}
      </div>
      {rows.length === 0 ? (
        <div className="py-5 px-4 text-center text-xs text-muted-foreground">No samples yet.</div>
      ) : (
        <ul className="divide-y">
          {rows.map((s) => (
            <li key={s.id}>
              <button type="button" onClick={() => navigate(`/samples/${s.id}`)} className="w-full flex items-center justify-between gap-3 px-4 py-2.5 text-left hover:bg-muted/50">
                <div>
                  <div className="font-mono text-xs font-semibold">{s.request_number}</div>
                  <div className="text-[11px] text-muted-foreground">
                    {s.sent_at ? `Sent ${new Date(s.sent_at).toLocaleDateString(dateLocale(), { day: "2-digit", month: "short" })}${s.tracking_number ? ` · ${s.tracking_number}` : ""}` : `Requested ${new Date(`${s.requested_on}T00:00:00`).toLocaleDateString(dateLocale(), { day: "2-digit", month: "short" })}`}
                  </div>
                </div>
                <SampleStatusBadge status={s.sample_status} />
              </button>
            </li>
          ))}
        </ul>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-xl">
          <DialogHeader>
            <DialogTitle>Request a sample or trial</DialogTitle>
            <DialogDescription>{mode === "DIGITAL" ? "What the customer gets to try. You add how they get in when it starts." : "What is going to the customer. You add the courier when it is sent."}</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-3 py-1">
            <div className="inline-flex rounded-md bg-muted p-0.5" role="group" aria-label="How it is delivered">
              {([["SHIPPED", "Shipped by courier"], ["DIGITAL", "Trial, demo or POC (remote)"]] as const).map(([k, label]) => (
                <button key={k} type="button" aria-pressed={mode === k} onClick={() => setMode(k)} className={`h-7 rounded-[5px] px-3 text-xs transition-colors ${mode === k ? "bg-card font-semibold text-foreground shadow-xs" : "text-muted-foreground hover:text-foreground"}`}>{label}</button>
              ))}
            </div>
            {items.map((r) => (
              <div key={r.key} className="grid grid-cols-[1fr_70px_70px_28px] items-end gap-2">
                <div className="space-y-1">
                  <Label className="text-xs">Item</Label>
                  {hasPermission("product.read") ? (
                    <SearchSelect
                      endpoint="/api/Product/List"
                      extraParams={{ active: "true" }}
                      value={r.product_id}
                      selectedLabel={r.product_id ? r.label : r.custom ? `Other: ${r.custom}` : ""}
                      getLabel={(row) => `${row.name} (${row.code})`}
                      placeholder="Search products"
                      allowEmpty
                      emptyLabel="-- Not in the catalog --"
                      onChange={(id, row) => setRow(r.key, { product_id: id, label: row ? row.name : "", unit: row ? row.unit : r.unit })}
                    />
                  ) : null}
                  {!r.product_id && <Input value={r.custom} onChange={(e) => setRow(r.key, { custom: e.target.value })} placeholder={mode === "DIGITAL" ? "Name what they get, e.g. 14-day trial, 10 users" : "Name what is being sent"} maxLength={200} className="h-9" />}
                </div>
                <div className="space-y-1"><Label className="text-xs">Qty</Label><Input type="number" min={0.01} step="0.01" value={r.quantity} onChange={(e) => setRow(r.key, { quantity: e.target.value })} className="h-9" /></div>
                <div className="space-y-1"><Label className="text-xs">Unit</Label><Input value={r.unit} onChange={(e) => setRow(r.key, { unit: e.target.value })} maxLength={20} className="h-9" /></div>
                <Button type="button" variant="ghost" size="icon-sm" aria-label="Remove item" disabled={items.length === 1} onClick={() => setItems((p) => p.filter((x) => x.key !== r.key))}><Trash2 className="size-3.5" /></Button>
              </div>
            ))}
            <Button type="button" variant="outline" size="sm" onClick={() => setItems((p) => [...p, blank(Math.max(...p.map((x) => x.key)) + 1)])}><Plus className="size-3.5 mr-1" />Add item</Button>
            {mode === "SHIPPED" && <div className="space-y-1.5"><Label>Send to</Label><Textarea rows={2} value={shipTo} onChange={(e) => setShipTo(e.target.value)} placeholder="Defaults to the customer's address" maxLength={1000} /></div>}
            <div className="space-y-1.5"><Label>Note</Label><Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="What they want to test" maxLength={2000} /></div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={busy}>{busy && <Loader2 className="size-4 animate-spin mr-2" />}{mode === "DIGITAL" ? "Request trial" : "Request sample"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
